import {useCallback, useEffect, useRef, useState} from 'react';
import {getUserRole, getReportAccessMap} from '../../utils/api/reportAccess';
import {useStompSubscription} from '../../hooks/useStompSubscription';

/**
 * Hook: subscribe to report access.
 * Fetches initial value via REST and keeps it live via STOMP.
 *
 * Mirrors legacy RTDB subscription useAccessSubscription API:
 *   { value, isLoading, error, refresh }
 *
 * - With userId: value is the role string (e.g., 'owner') or null
 * - Without userId: value is the full access map {userId: role}
 * - refresh(): re-fetches from REST without resetting isLoading (use after mutations)
 */
export const useAccessSubscription = (reportId, userId = undefined, options = {}) => {
    const {initialValue = null} = options;
    const [value, setValue] = useState(initialValue);
    const [isLoading, setIsLoading] = useState(Boolean(reportId));
    const [error, setError] = useState(null);
    const mountedRef = useRef(true);

    // Initial fetch
    useEffect(() => {
        mountedRef.current = true;
        setError(null);
        setValue(initialValue);
        setIsLoading(Boolean(reportId));

        if (!reportId) {
            setIsLoading(false);
            return;
        }

        const load = async () => {
            try {
                const result = userId
                    ? await getUserRole(reportId, userId)
                    : await getReportAccessMap(reportId);
                if (mountedRef.current) {
                    setValue(result);
                    setIsLoading(false);
                }
            } catch (err) {
                console.error('useAccessSubscription fetch error:', err);
                if (mountedRef.current) {
                    setError(err);
                    setIsLoading(false);
                }
            }
        };

        load();

        return () => {
            mountedRef.current = false;
        };
    }, [reportId, userId, initialValue]);

    // Silent re-fetch — does not reset isLoading; used after mutations and by STOMP relay.
    const refresh = useCallback(() => {
        if (!reportId || !mountedRef.current) return;
        const reload = userId
            ? getUserRole(reportId, userId)
            : getReportAccessMap(reportId);
        reload.then((result) => {
            if (mountedRef.current) setValue(result);
        }).catch((err) => {
            console.warn('useAccessSubscription reload error:', err);
        });
    }, [reportId, userId]);

    // Live STOMP updates — re-fetch on any access change event
    useStompSubscription(
        reportId ? `/topic/reports/${reportId}/access` : null,
        refresh
    );

    return {value, isLoading, error, refresh};
};
